import { queryClient } from "@/lib/queryClient";

/** Consistent React Query workspace segment for list caches (personal vs shared workspace). */
export function workspaceScopeKey(workspaceId: string | null | undefined): string {
  return workspaceId ?? "personal";
}

// ── DS EDIT 6: cross-tab sync + logout purge ────────────────────────────────
// Thread-list caches live in React Query, keyed per lane. When a mutation in
// one tab bumps a thread, other tabs mark their lanes stale (invalidate → SWR
// revalidate, no purge). On logout every lane is REMOVED in every tab so the
// next account never sees the previous account's thread titles (Sensitive
// tier guardrail).

const THREAD_CACHE_CHANNEL = "deeplysmart-thread-cache";
const STORAGE_SYNC_KEY = "ds-thread-cache-sync";
const STORAGE_PURGE_KEY = "ds-thread-cache-purge";

/** Every thread-list query-key root across the app's lanes. */
const THREAD_CACHE_KEY_ROOTS: readonly (readonly string[])[] = [
  ["chat-thread-summaries"],
  ["/api/chat-thread-summaries"],
  ["accessible-thread-summaries"],
  ["/api/chat-threads"],
  ["threads", "list"],
];

let channel: BroadcastChannel | null = null;
let lifecycleInstalled = false;

function invalidateThreadLanesLocally(): void {
  for (const key of THREAD_CACHE_KEY_ROOTS) {
    queryClient.invalidateQueries({ queryKey: key as unknown as string[], exact: false });
  }
}

function purgeThreadLanesLocally(): void {
  for (const key of THREAD_CACHE_KEY_ROOTS) {
    queryClient.removeQueries({ queryKey: key as unknown as string[], exact: false });
  }
}

function getChannel(): BroadcastChannel | null {
  if (channel) return channel;
  try {
    if (typeof BroadcastChannel === "undefined") return null;
    channel = new BroadcastChannel(THREAD_CACHE_CHANNEL);
    channel.onmessage = (event: MessageEvent) => {
      const type = (event.data as { type?: string } | null)?.type;
      if (type === "invalidate") invalidateThreadLanesLocally();
      if (type === "purge") purgeThreadLanesLocally();
    };
  } catch {
    channel = null;
  }
  return channel;
}

/** Notify other tabs that thread lists changed (they revalidate, not purge). */
export function broadcastThreadCacheInvalidation(): void {
  try {
    getChannel()?.postMessage({ type: "invalidate" });
    // storage-event fallback for browsers without BroadcastChannel
    window.localStorage.setItem(STORAGE_SYNC_KEY, String(Date.now()));
  } catch {
    // cross-tab sync is best-effort; local cache is already correct
  }
}

/** Purge every thread-list lane locally and in other tabs (logout / session expiry). */
export function purgeThreadCaches(options?: { broadcast?: boolean }): void {
  purgeThreadLanesLocally();
  if (options?.broadcast === false) return;
  try {
    getChannel()?.postMessage({ type: "purge" });
    window.localStorage.setItem(STORAGE_PURGE_KEY, String(Date.now()));
  } catch {
    // best-effort
  }
}

/** Install listeners once (module import in browser context). */
function installThreadCacheLifecycle(): void {
  if (lifecycleInstalled || typeof window === "undefined") return;
  lifecycleInstalled = true;
  getChannel();
  window.addEventListener("storage", (event) => {
    if (event.key === STORAGE_SYNC_KEY) invalidateThreadLanesLocally();
    if (event.key === STORAGE_PURGE_KEY) purgeThreadLanesLocally();
  });
}

installThreadCacheLifecycle();

export function patchThreadSummariesCache(
  updater: (item: Record<string, unknown>) => Record<string, unknown>
): void {
  queryClient.setQueriesData(
    { queryKey: ["/api/chat-thread-summaries"], exact: false },
    (old: unknown) => {
      if (!Array.isArray(old)) return old;
      return old.map((item) => updater(item as Record<string, unknown>));
    }
  );
}

export function bumpThreadInSummariesCache(
  threadId: string,
  updatedAt: string = new Date().toISOString()
): void {
  patchThreadSummariesCache((item) =>
    item?.id === threadId ? { ...item, updatedAt } : item
  );
  broadcastThreadCacheInvalidation();
}

export function prependThreadToSummariesCache(thread: Record<string, unknown>): void {
  queryClient.setQueriesData(
    { queryKey: ["/api/chat-thread-summaries"], exact: false },
    (old: unknown) => {
      if (!Array.isArray(old)) return old;
      const id = thread.id;
      if (id != null && old.some((item: { id?: string }) => item?.id === id)) {
        return old;
      }
      return [thread, ...old];
    }
  );
  broadcastThreadCacheInvalidation();
}

export function patchSpacesCache(
  updater: (space: Record<string, unknown>) => Record<string, unknown>
): void {
  queryClient.setQueriesData({ queryKey: ["/api/spaces"], exact: false }, (old: unknown) => {
    if (!Array.isArray(old)) return old;
    return old.map((space) => updater(space as Record<string, unknown>));
  });
}

export function bumpSpaceInCache(
  spaceId: string,
  updatedAt: string = new Date().toISOString()
): void {
  patchSpacesCache((space) =>
    space?.id === spaceId ? { ...space, updatedAt } : space
  );
}
